import dayjs from 'dayjs';
import { allocateDiscount, KT_MACHINES, hasTeam, staffLabel, TEAM_ROLE, TEAM_ROLES } from './ktCards';
import {
  billTotal, dmy, fmtMoney, matchBank, matchPayoo, needsRef, PAY_METHOD, REF_LABEL,
  type Bill, type BillDraft, type BillLine, type DraftLine, type KtState, type PayMethod, type Payment, type Service, type Shift, type ShiftTxn, type Team,
} from './ktLogic';

/**
 * Kế toán quầy, Sale Offline (SRS 3.1 đến 3.3): hóa đơn tạm tính, thu đa hình thức,
 * phiếu phụ thu, biên bản chốt ca, ghi ca thực hiện. Toàn bộ là hàm thuần. Không có ví: khách trả bằng tiền thật.
 */

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const esc = (t: string | number) => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

// ===================== Tạm tính =====================

/** Chia chiết khấu tổng đơn về từng dòng để bill ghi đúng tiền nét */
export function draftLines(d: Pick<BillDraft, 'lines' | 'orderDiscount'>): BillLine[] {
  const nets = d.lines.map(l => l.list - l.lineDiscount);
  const shares = allocateDiscount(nets, d.orderDiscount);
  return d.lines.map((l, i) => ({ id: l.id, serviceId: l.serviceId, name: l.name, gross: nets[i] - shares[i], list: l.list, lineDiscount: l.lineDiscount, orderDiscount: shares[i] }));
}
export const draftTotal = (d: Pick<BillDraft, 'lines' | 'orderDiscount'>) => sum(d.lines.map(l => l.list - l.lineDiscount)) - d.orderDiscount;

/** Dịch vụ, sản phẩm bán tại quầy: gói liệu trình bán trên POS để sinh thẻ; phụ thu có luồng riêng */
export const sellable = (services: Service[]) => services.filter(x => x.type !== 'package' && x.group !== 'Phụ thu');

/** Lưu nháp chỉ cần biết khách và dịch vụ */
export function draftSaveErrors(d: Pick<BillDraft, 'customer' | 'lines' | 'orderDiscount'>): string[] {
  const errors: string[] = [];
  if (!d.customer.trim()) errors.push('Nhập tên khách');
  if (!d.lines.length) errors.push('Thêm ít nhất một dịch vụ');
  if (d.lines.some((l: DraftLine) => l.lineDiscount < 0 || l.lineDiscount > l.list)) errors.push('Chiết khấu dòng không được lớn hơn giá');
  if (d.orderDiscount < 0 || d.orderDiscount > sum(d.lines.map(l => l.list - l.lineDiscount))) errors.push('Chiết khấu tổng đơn không được lớn hơn tiền hàng');
  return errors;
}

/** Mã giao dịch đã dùng ở bill khác hoặc lặp trong chính lần thu này */
export function paymentErrors(s: Pick<KtState, 'bills'>, payments: Payment[], total: number, exceptBill?: string): string[] {
  const errors: string[] = [];
  const paid = sum(payments.map(p => p.amount));
  if (!payments.length) errors.push('Chưa nhập hình thức thu');
  if (paid !== total) errors.push(`Tổng thu ${fmtMoney(paid)} phải đúng bằng tiền nét ${fmtMoney(total)}`);
  const seen = new Set<string>();
  const used = new Set(s.bills.filter(b => b.status === 'valid' && b.id !== exceptBill).flatMap(b => b.payments.map(p => p.ref.trim().toUpperCase())).filter(Boolean));
  payments.forEach(p => {
    if (!(p.amount > 0)) errors.push(`${PAY_METHOD[p.method]}: số tiền phải lớn hơn 0`);
    if (needsRef(p.method) && !p.ref.trim()) errors.push(`${PAY_METHOD[p.method]}: bắt buộc nhập ${REF_LABEL[p.method]}`);
    const ref = p.ref.trim().toUpperCase();
    if (ref && (seen.has(ref) || used.has(ref))) errors.push(`Mã ${ref} đã dùng cho giao dịch khác`);
    if (ref) seen.add(ref);
    if (p.method === 'prepaid') errors.push('Tại quầy không thu bằng ví');
  });
  return errors;
}

/** Điều kiện thu chính thức: đúng tiền nét, đủ mã */
export function draftPayErrors(s: Pick<KtState, 'bills'>, d: BillDraft): string[] {
  return [...draftSaveErrors(d), ...paymentErrors(s, d.payments, draftTotal(d))];
}

// ===================== Phiếu phụ thu =====================

export type SurchargeType = 'pain' | 'test' | 'installment' | 'vat' | 'other';
export const SURCHARGE: Record<SurchargeType, { label: string; serviceId: string }> = {
  pain: { label: 'Phí giảm đau', serviceId: 'PT-GIAMDAU' },
  test: { label: 'Phí xét nghiệm', serviceId: 'PT-XETNGHIEM' },
  installment: { label: 'Phụ thu trả góp', serviceId: 'PT-TRAGOP' },
  vat: { label: 'Phí VAT', serviceId: 'PT-VAT' },
  other: { label: 'Phụ thu khác', serviceId: 'PT-KHAC' },
};

// ===================== Nhật ký sửa, hủy =====================

/** Lưu vết tuyệt đối: chỉ thêm dòng, không có thao tác xóa hay sửa nhật ký */
export function auditRows(s: Pick<KtState, 'bills'>) {
  return s.bills.flatMap(b => b.log.filter(l => l.action === 'Sửa bill' || l.action === 'Hủy bill').map((l, i) => ({ key: `${b.id}-${i}`, billId: b.id, billNo: b.no, customer: b.customer, date: b.date, ...l })))
    .sort((a, b) => b.at - a.at);
}

// ===================== Chốt ca =====================

export const METHOD_ORDER: PayMethod[] = ['cash', 'transfer', 'vietqr', 'payoo', 'installment', 'prepaid'];

export function shiftSummary(txns: ShiftTxn[]) {
  const byMethod = (m: PayMethod) => sum(txns.filter(t => t.method === m).map(t => t.amount));
  return {
    rows: METHOD_ORDER.map(method => ({ method, label: PAY_METHOD[method], amount: byMethod(method), count: txns.filter(t => t.method === method).length })).filter(r => r.count > 0),
    cash: byMethod('cash'),
    total: sum(txns.map(t => t.amount)),
    missing: txns.filter(t => needsRef(t.method) && t.kind === 'receipt' && !t.ref.trim()),
  };
}

export const bankTxns = (txns: ShiftTxn[]) => txns.filter(t => t.method === 'transfer' || t.method === 'vietqr');
export const payooTxns = (txns: ShiftTxn[]) => txns.filter(t => t.method === 'payoo' || t.method === 'installment');

const txnSheet = (txns: ShiftTxn[]) => txns.map(t => ({
  'Số bill': t.billNo, Ngày: dmy(t.date), Khách: t.customer, 'Hình thức': PAY_METHOD[t.method], Loại: t.kind === 'refund' ? 'Hoàn tiền' : 'Thu', 'Số tiền': t.amount, 'Mã giao dịch': t.ref,
}));

/** Biên bản và 2 bảng kê gửi Kế toán bán hàng: mỗi phần tử là một sheet Excel */
export function shiftSheets(x: Shift): { name: string; rows: Record<string, string | number>[] }[] {
  const sum0 = shiftSummary(x.txns);
  return [
    { name: 'Bien ban chot ca', rows: [
      { 'Nội dung': 'Số ca', 'Giá trị': x.no },
      { 'Nội dung': 'Ca', 'Giá trị': x.name },
      { 'Nội dung': 'Người chốt', 'Giá trị': x.by },
      { 'Nội dung': 'Thời điểm', 'Giá trị': dayjs(x.at).format('HH:mm DD/MM/YYYY') },
      ...sum0.rows.map(r => ({ 'Nội dung': `Thu ${r.label} (${r.count} giao dịch)`, 'Giá trị': r.amount })),
      { 'Nội dung': 'Quỹ đầu ca', 'Giá trị': x.openingCash },
      { 'Nội dung': 'Tiền mặt theo sổ', 'Giá trị': x.cashExpected },
      { 'Nội dung': 'Tiền mặt kiểm kê', 'Giá trị': x.cashCounted },
      { 'Nội dung': 'Chênh lệch', 'Giá trị': x.diff },
      { 'Nội dung': 'Lý do chênh lệch', 'Giá trị': x.diffReason },
    ] },
    { name: 'Bang ke Ngan hang', rows: txnSheet(bankTxns(x.txns)) },
    { name: 'Bang ke Payoo', rows: txnSheet(payooTxns(x.txns)) },
  ];
}

const txnTable = (title: string, txns: ShiftTxn[]) => `<h2>${esc(title)}</h2>${txns.length ? `<table class="grid"><tr><th>Số bill</th><th>Khách</th><th>Hình thức</th><th class="r">Số tiền</th><th>Mã giao dịch</th></tr>${txns.map(t => `<tr><td>${esc(t.billNo)}</td><td>${esc(t.customer)}</td><td>${esc(PAY_METHOD[t.method])}${t.kind === 'refund' ? ' (hoàn)' : ''}</td><td class="r">${esc(fmtMoney(t.amount))}</td><td>${esc(t.ref)}</td></tr>`).join('')}</table>` : '<div class="muted">Không có giao dịch</div>'}`;

export function shiftHtml(x: Shift) {
  const t = shiftSummary(x.txns);
  return `<h1>BIÊN BẢN CHỐT CA ${esc(x.no)}</h1>
  <div class="c muted">${esc(x.name)} · ${esc(dayjs(x.at).format('HH:mm DD/MM/YYYY'))} · Người chốt: ${esc(x.by)}</div>
  <h2>Tổng hợp</h2>
  <table class="grid"><tr><th>Hình thức</th><th class="r">Số giao dịch</th><th class="r">Số tiền</th></tr>${t.rows.map(r => `<tr><td>${esc(r.label)}</td><td class="r">${r.count}</td><td class="r">${esc(fmtMoney(r.amount))}</td></tr>`).join('')}<tr><td><b>Tổng</b></td><td></td><td class="r"><b>${esc(fmtMoney(t.total))}</b></td></tr></table>
  <h2>Kiểm kê tiền mặt</h2>
  <table class="grid">
    <tr><td>Quỹ đầu ca</td><td class="r">${esc(fmtMoney(x.openingCash))}</td></tr>
    <tr><td>Tiền mặt theo sổ</td><td class="r">${esc(fmtMoney(x.cashExpected))}</td></tr>
    <tr><td>Tiền mặt kiểm kê</td><td class="r">${esc(fmtMoney(x.cashCounted))}</td></tr>
    <tr><td><b>Chênh lệch</b></td><td class="r"><b>${esc(fmtMoney(x.diff))}</b></td></tr>
    ${x.diffReason ? `<tr><td>Lý do</td><td>${esc(x.diffReason)}</td></tr>` : ''}
  </table>
  ${txnTable('Bảng kê Ngân hàng', bankTxns(x.txns))}
  ${txnTable('Bảng kê Payoo', payooTxns(x.txns))}
  <div class="sign"><div>Người chốt ca<i>Ký, ghi rõ họ tên</i>${esc(x.by)}</div><div>Kế toán bán hàng<i>Ký, ghi rõ họ tên</i>${esc(x.receivedBy || '')}</div></div>`;
}

const moneyLines = (lines: { name: string; gross: number; list?: number; lineDiscount?: number; orderDiscount?: number }[]) => `<table class="grid"><tr><th>Dịch vụ</th><th class="r">Giá</th><th class="r">Chiết khấu</th><th class="r">Thành tiền</th></tr>${lines.map(l => `<tr><td>${esc(l.name)}</td><td class="r">${esc(fmtMoney(l.list ?? l.gross))}</td><td class="r">${esc(fmtMoney((l.lineDiscount || 0) + (l.orderDiscount || 0)))}</td><td class="r">${esc(fmtMoney(l.gross))}</td></tr>`).join('')}</table>`;

export function draftHtml(d: BillDraft) {
  return `<h1>HÓA ĐƠN TẠM TÍNH ${esc(d.no)}</h1>
  <div class="c muted">Không phải chứng từ thu tiền · ${esc(dayjs(d.updatedAt).format('HH:mm DD/MM/YYYY'))}</div>
  <p>Khách hàng: <b>${esc(d.customer)}</b></p>${moneyLines(draftLines(d))}
  <p class="c"><b>Tổng tiền nét: ${esc(fmtMoney(draftTotal(d)))}</b></p>`;
}

export function billHtml(b: Bill) {
  return `<h1>BILL THU TIỀN ${esc(b.no)}</h1>
  <div class="c muted">${esc(dayjs(b.at).format('HH:mm DD/MM/YYYY'))} · Thu ngân: ${esc(b.createdBy)}</div>
  <p>Khách hàng: <b>${esc(b.customer)}</b></p>${moneyLines(b.lines)}
  <p class="c"><b>Tổng tiền nét: ${esc(fmtMoney(billTotal(b)))}</b></p>
  <table class="grid"><tr><th>Hình thức</th><th class="r">Số tiền</th><th>Mã giao dịch</th></tr>${b.payments.map(p => `<tr><td>${esc(PAY_METHOD[p.method])}</td><td class="r">${esc(fmtMoney(p.amount))}</td><td>${esc(p.ref)}</td></tr>`).join('')}</table>`;
}

// ===================== Đối soát đơn POS đã thanh toán =====================

export type PosCheck = 'dup_ref' | 'missing_ref' | 'amount' | 'waiting' | 'cash' | 'none' | 'matched';
export type PosGroup = 'attention' | 'waiting' | 'done';
export const POS_CHECK: Record<PosCheck, { label: string; color: string; group: PosGroup }> = {
  dup_ref: { label: 'Trùng mã giao dịch', color: 'red', group: 'attention' },
  missing_ref: { label: 'Thiếu mã giao dịch', color: 'red', group: 'attention' },
  amount: { label: 'Lệch số tiền', color: 'red', group: 'attention' },
  waiting: { label: 'Chờ tiền về', color: 'gold', group: 'waiting' },
  cash: { label: 'Đếm khi chốt ca', color: 'blue', group: 'done' },
  none: { label: 'Không cần đối soát', color: 'default', group: 'done' },
  matched: { label: 'Đã khớp tiền về', color: 'green', group: 'done' },
};
export const checkGroup = (c: PosCheck) => POS_CHECK[c].group;
/** Một đơn có nhiều giao dịch: đơn mang kết quả nặng nhất */
const CHECK_ORDER: PosCheck[] = ['dup_ref', 'missing_ref', 'amount', 'waiting', 'cash', 'none', 'matched'];

const normRef = (r: string) => r.trim().toUpperCase();
const isPayooPay = (m: PayMethod) => m === 'payoo' || m === 'installment';

/** Đơn POS đã thu đủ và chưa hoàn hết: đơn còn nợ, đơn hủy không tính là thanh toán thành công */
export const isPosPaid = (b: Bill) => {
  const paid = sum(b.payments.map(p => p.amount));
  return !!b.posOrderId && b.status === 'valid' && b.debt <= 0 && paid > 0 && sum(b.refunds.map(r => r.amount)) < paid;
};

export const posPaidDates = (bills: Bill[]) => Array.from(new Set(bills.filter(isPosPaid).map(b => b.date))).sort().reverse();

export interface PosPayCheck { payment: Payment; check: PosCheck }
export interface PosRow { bill: Bill; paid: number; refunded: number; pays: PosPayCheck[]; check: PosCheck }

/** Đơn POS đã thanh toán của một ngày, kèm kết quả đối soát từng giao dịch với file Kế toán nội bộ đã nạp */
export function posPaidRows(s: Pick<KtState, 'bills' | 'payoo' | 'bank'>, date: string): PosRow[] {
  const payoo = matchPayoo(s.payoo, s.bills);
  const bank = matchBank(s.bank, s.bills);
  const notInFile = new Set([...payoo.missing, ...bank.missing].map(x => x.p.id));
  const refCount = new Map<string, number>();
  s.bills.filter(b => b.status === 'valid').forEach(b => b.payments.forEach(p => { if (p.ref.trim()) refCount.set(normRef(p.ref), (refCount.get(normRef(p.ref)) || 0) + 1); }));

  const checkOf = (b: Bill, p: Payment): PosCheck => {
    if (p.method === 'cash') return 'cash';
    if (!needsRef(p.method)) return 'none';
    if (!p.ref.trim()) return 'missing_ref';
    if ((refCount.get(normRef(p.ref)) || 0) > 1) return 'dup_ref';
    if (notInFile.has(p.id)) return 'waiting';
    const mismatch = isPayooPay(p.method)
      ? payoo.matches.some(m => m.bill?.id === b.id && m.status === 'amount' && normRef(m.row.ref) === normRef(p.ref))
      : bank.matches.some(m => m.payId === p.id && m.status === 'amount');
    return mismatch ? 'amount' : 'matched';
  };

  return s.bills.filter(b => b.date === date && isPosPaid(b)).map(b => {
    const pays = b.payments.map(payment => ({ payment, check: checkOf(b, payment) }));
    return {
      bill: b, pays, paid: sum(b.payments.map(p => p.amount)), refunded: sum(b.refunds.map(r => r.amount)),
      check: CHECK_ORDER.find(c => pays.some(x => x.check === c)) || 'none',
    };
  }).sort((a, b) => b.bill.at - a.bill.at);
}

export function posPaidSummary(rows: PosRow[]) {
  const pays = rows.flatMap(r => r.pays);
  const nonCash = pays.filter(x => needsRef(x.payment.method));
  const count = (g: PosGroup) => rows.filter(r => checkGroup(r.check) === g).length;
  return {
    total: sum(rows.map(r => r.paid)),
    cash: sum(pays.filter(x => x.payment.method === 'cash').map(x => x.payment.amount)),
    nonCash: sum(nonCash.map(x => x.payment.amount)),
    nonCashCount: nonCash.length,
    matchedCount: nonCash.filter(x => x.check === 'matched').length,
    attention: count('attention'), waiting: count('waiting'), done: count('done'),
  };
}

// ===================== Ca thực hiện =====================

const groupOf = (s: Pick<KtState, 'services'>, serviceId: string) => s.services.find(x => x.id === serviceId)?.group || '';
const machineNeeded = (group: string) => KT_MACHINES.some(m => m.group === group);

export const teamText = (t?: Team) => TEAM_ROLES.flatMap(r => (t?.[r] ? [`${TEAM_ROLE[r]}: ${staffLabel(t[r]!)}`] : [])).join(', ');

export interface SessionRow { key: string; billId: string; lineId: string; docNo: string; date: string; customer: string; service: string; group: string; team?: Team; machine: string; shots?: number; recorded: boolean }

const isRecorded = (team: Team | undefined, machine: string, groups: string[]) => hasTeam(team) && (!groups.some(machineNeeded) || !!machine);

/** Dòng dịch vụ lẻ của một ngày, sản phẩm không cần ca */
export function sessionRows(s: Pick<KtState, 'bills' | 'services'>, date: string): SessionRow[] {
  return s.bills.filter(b => b.status === 'valid' && b.kind === 'service' && b.date === date).flatMap(b => b.lines.filter(l => s.services.find(x => x.id === l.serviceId)?.type !== 'product').map(l => {
    const group = groupOf(s, l.serviceId);
    const team = l.team || b.team;
    return { key: `${b.id}-${l.id}`, billId: b.id, lineId: l.id, docNo: b.no, date: b.date, customer: b.customer, service: l.name, group, team, machine: l.machine || '', shots: l.shots, recorded: isRecorded(team, l.machine || '', [group]) };
  }));
}

/** Căn cứ khấu hao theo máy trong kỳ: số ca và số shot đã ghi */
export function machineRows(s: Pick<KtState, 'bills' | 'usageLog' | 'services' | 'cards'>, ym: string) {
  const map = new Map(KT_MACHINES.map(m => [m.code, { ...m, sessions: 0, shots: 0 }]));
  const add = (code: string, shots = 0) => { const r = map.get(code); if (r) { r.sessions += 1; r.shots += shots; } };
  let unassigned = 0;
  s.bills.filter(b => b.status === 'valid' && b.kind === 'service' && b.date.startsWith(ym)).forEach(b => b.lines.forEach(l => {
    if (l.machine) add(l.machine, l.shots);
    else if (machineNeeded(groupOf(s, l.serviceId))) unassigned += 1;
  }));
  s.usageLog.filter(e => !e.cancelled && e.date.startsWith(ym)).forEach(e => {
    if (e.machine) { add(e.machine, e.shots); return; }
    const card = s.cards.find(c => c.id === e.cardId);
    if (e.items.some(i => machineNeeded(card?.lines.find(l => l.serviceId === i.serviceId)?.group || ''))) unassigned += 1;
  });
  return { rows: Array.from(map.values()), unassigned };
}
